// ── Tenant-Scoped Access Helpers ──
// Data-layer enforcement for multi-tenant isolation.
// These are the PRIMARY access patterns for tenant-scoped queries.

import type { ExternalClient, TenantAccessResult, TenantOwned } from "./types";
import type { Job } from "@/data/types";
import type { Opportunity } from "@/data/opportunityTypes";
import type { ManagedUser, ManagedTenant } from "@/data/controlPlane/types";

// ── Core Access Assertion ──

/**
 * Asserts that a user's tenant matches the record's tenant.
 * This is the canonical access gate — all tenant-scoped queries should use this.
 */
export function assertTenantAccess(
  userTenantId: string,
  recordTenantId: string | undefined
): TenantAccessResult {
  // Records without tenantId are legacy/unscoped — allow during migration
  if (!recordTenantId) {
    return { allowed: true, reason: "Record has no tenant scope (legacy)" };
  }
  if (userTenantId === recordTenantId) {
    return { allowed: true, reason: "Tenant match" };
  }
  return { allowed: false, reason: `Tenant mismatch: user=${userTenantId}, record=${recordTenantId}` };
}

/**
 * Returns true if the user is a cross-tenant operator (super_admin/owner of internal tenant).
 */
export function isCrossTenantOperator(user: ManagedUser): boolean {
  return user.role === "super_admin" || user.role === "owner";
}

// ── Scoped Query Helpers ──

/**
 * Filters any tenant-owned record array by tenantId.
 * Returns all records if tenantId is null (cross-tenant mode).
 */
export function filterByTenant<T extends TenantOwned>(
  records: T[],
  tenantId: string | null
): T[] {
  if (!tenantId) return records;
  return records.filter((r) => !r.tenantId || r.tenantId === tenantId);
}

/**
 * Get external clients scoped to a tenant.
 */
export function getClientsForTenant(
  clients: ExternalClient[],
  tenantId: string
): ExternalClient[] {
  return clients.filter((c) => c.tenantId === tenantId);
}

/**
 * Get jobs scoped to a tenant.
 * Jobs use optional tenantId for migration safety.
 */
export function getJobsForTenant(
  jobs: Job[],
  tenantId: string
): Job[] {
  return jobs.filter((j) => (j as TenantOwned).tenantId === tenantId || !(j as TenantOwned).tenantId);
}

/**
 * Get opportunities scoped to a tenant.
 */
export function getOpportunitiesForTenant(
  opps: Opportunity[],
  tenantId: string
): Opportunity[] {
  return opps.filter((o) => (o as TenantOwned).tenantId === tenantId || !(o as TenantOwned).tenantId);
}

/**
 * Get users belonging to a specific tenant.
 */
export function getUsersForTenant(
  users: ManagedUser[],
  tenantId: string
): ManagedUser[] {
  return users.filter((u) => u.tenantId === tenantId);
}

/**
 * Get a tenant by ID.
 */
export function getTenantById(
  tenants: ManagedTenant[],
  tenantId: string
): ManagedTenant | undefined {
  return tenants.find((t) => t.id === tenantId);
}

/**
 * Build a tenant-scoped summary for quick reference.
 */
export function buildTenantSummary(
  tenantId: string,
  tenants: ManagedTenant[],
  users: ManagedUser[],
  clients: ExternalClient[],
  jobs: Job[]
) {
  const tenant = getTenantById(tenants, tenantId);
  return {
    tenant,
    userCount: getUsersForTenant(users, tenantId).length,
    clientCount: getClientsForTenant(clients, tenantId).length,
    jobCount: getJobsForTenant(jobs, tenantId).length,
  };
}
